
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface UseSupabaseAvailabilityResult {
  unavailableDates: Date[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useSupabaseAvailability = (propertyId: string): UseSupabaseAvailabilityResult => {
  const { data: unavailableDates = [], isLoading, error, refetch } = useQuery({
    queryKey: ['property-availability', propertyId],
    queryFn: async (): Promise<Date[]> => {
      console.log(`📅 useSupabaseAvailability: Fetching availability for property ${propertyId} from Supabase...`);
      
      if (!propertyId) {
        console.log('❌ useSupabaseAvailability: No property ID provided');
        return [];
      }
      
      // CORRECCIÓN: Obtener SOLO fechas bloqueadas manualmente, NO reservaciones
      console.log(`🔍 useSupabaseAvailability: Querying property_blocked_days table with property_id = '${propertyId}'`);
      
      const { data: blockedDays, error: blockedError } = await supabase
        .from('property_blocked_days')
        .select('blocked_date')
        .eq('property_id', propertyId)
        .order('blocked_date', { ascending: true });

      if (blockedError) {
        console.error('❌ useSupabaseAvailability: Error fetching blocked days:', blockedError);
        throw blockedError;
      }

      console.log(`📊 useSupabaseAvailability: Raw blocked days from DB for property ${propertyId}:`, {
        blockedDays: blockedDays?.length || 0
      });

      const unavailableDates: Date[] = [];

      // Procesar SOLO fechas bloqueadas manualmente
      blockedDays?.forEach(item => {
        const dateString = item.blocked_date;
        console.log(`🔍 useSupabaseAvailability: Processing blocked date: ${dateString}`);
        
        const [year, month, day] = dateString.split('-').map(Number);
        const localDate = new Date(year, month - 1, day);
        unavailableDates.push(localDate);
      });
      
      console.log(`✅ useSupabaseAvailability: Fetched ${unavailableDates.length} manually blocked dates from Supabase for property ${propertyId}`);
      
      return unavailableDates;
    },
    enabled: !!propertyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  return {
    unavailableDates,
    isLoading,
    error: error ? String(error) : null,
    refetch
  };
};

// Hook to trigger iCal sync for a property
export const useSyncPropertyAvailability = () => {
  return async (propertyId: string, icalUrl: string) => {
    console.log(`🔄 Triggering iCal sync for property ${propertyId}`);
    
    try {
      const { data, error } = await supabase.functions.invoke('sync-ical', {
        body: { propertyId, icalUrl }
      });

      if (error) {
        console.error('Error syncing availability:', error);
        throw error;
      }

      console.log('✅ Successfully synced availability:', data);
      return data;
    } catch (error) {
      console.error('Error in sync function:', error);
      throw error;
    }
  };
};
